import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {isStreetValid} from '~shared/directives/form-validators/is-street-valid.directive';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {isIBANValid, noWhitespaceCharacters} from '~shared/directives/form-validators';
import {Bank, Country, Customer, GooglePlacesAddress} from '~core/models';
import {BankService, DefaultNotificationService, StorageService} from '~core/services';
import {CustomerOverviewTranslationsService} from '~shared/translations';

/**
 * Handles bank data management.
 */
@UntilDestroy()
@Component({
  selector: 'app-bank-data',
  templateUrl: './bank-data.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class BankDataComponent implements OnInit, OnDestroy {
  public createBankDataForm: UntypedFormGroup;
  public selectedBankData: Bank;
  public isIbanValid: boolean = false;
  public apiCustomerPrefix: string = '/api/customers/';
  public spinnerName: string = 'createBankingData';
  public countries: Array<Country> = [];
  public hasAddress: boolean = false;
  public pageTitle: string;

  @Input() public customer: Customer;

  constructor(
    private notificationService: DefaultNotificationService,
    private formBuilder: UntypedFormBuilder,
    private bankService: BankService,
    private customerOverviewTranslations: CustomerOverviewTranslationsService,
    private spinnerService: SpinnerService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public customerOverviewService: CustomerOverViewService,
    private storage: StorageService,
  ) {
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe(countries => this.countries = countries);
  }

  /**
   * Create the form and fetch current user
   */
  public ngOnInit(): any {
    this.pageTitle = $localize`:@@customerOverviewComponent.modalTitle.createBankingData:Create banking data`;
    this.fillBankDataForm();
  }

  /**
   * Clear old values from the behavior subject observable.
   */
  public ngOnDestroy(): void {
  }

  /**
   * Fill the bank data form.
   */
  public fillBankDataForm(): void {
    this.createBankDataForm = this.formBuilder.group({
      customer: `${this.apiCustomerPrefix}${this.customer.id}`,
      IBAN: [null, [
        Validators.required,
        noWhitespaceCharacters,
        Validators.maxLength(34),
        isIBANValid
      ]],
      BIC: [{value: null, disabled: true}],
      bankName: [{value: null, disabled: true}],
      address: null,
    });
  }

  /**
   * If bank form is valid update the bank data, otherwise display errors.
   */
  public createBankDataFormSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      this.spinnerService.show(this.spinnerName);
      this.bankService.create(form.getRawValue()).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.customerOverviewTranslations.i18nSuccessBankDataCreate
            });
            this.bankService.addToBankList(data);
            this.customerOverviewService.toggleAllModal(false);
          },
          error: (error: Error) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.customerOverviewTranslations.i18nErrorBankDataCreate
            });
          }
        });
    }
  }

  /**
   * The recieved data from the IBAN API will be passed in this function so we can fill the BIC and Bank name form properties
   */
  fillFormData(data: any): void {
    if (data) {
      this.isIbanValid = true;
      this.createBankDataForm.patchValue({
        IBAN: data.IBAN,
        BIC: data.BIC,
        bankName: data.bankName
      });
    } else {
      this.isIbanValid = false;
    }
  }

  /**
   * Update address values upon Google Places autocomplete address selection event.
   */
  public fillAddressValues(googlePlacesAddress: GooglePlacesAddress): void {
    this.createBankDataForm.get('address').patchValue(googlePlacesAddress);
  }

  /**
   * Updates the form with the new address values or removes them
   */
  public changeAccountHolder(differentHolder: boolean): void {
    this.hasAddress = differentHolder;

    this.createBankDataForm.setControl('address',
      !this.hasAddress
        ? new UntypedFormControl(null)
        : this.formBuilder.group({
          firstName: [null, Validators.required],
          lastName: [null, Validators.required],
          street: [null, [Validators.required, isStreetValid]],
          city: [null, Validators.required],
          postalCode: [null, Validators.required],
          country: ['DE', Validators.required],
        })
    );
  }

}
